/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import * as authApi from "@/api/auth";

interface User {
  id: number;
  username: string;
  totpEnabled?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<authApi.LoginResult>;
  verifyTotp: (pendingToken: string, code: string) => Promise<void>;
  register: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkRegistrationStatus: () => Promise<{
    hasUsers: boolean;
    oidcConfigured: boolean;
    oidcReady: boolean;
  }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Skip auth check if we're on the public route
    const isPublicRoute = window.location.pathname.includes('/public');

    if (isPublicRoute) {
      setIsLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const isVerified = await authApi.verify();
        if (isVerified) {
          const userInfo = await authApi.getUserInfo();
          setUser(userInfo.user);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // login/verifyTotp must not toggle the global isLoading: AuthRoute unmounts the
  // login page while it is true, which drops Login's local pendingToken and the
  // TOTP step never shows up.
  const login = async (username: string, password: string) => {
    const response = await authApi.login(username, password);
    if (!authApi.loginRequiresTotp(response)) {
      setUser(response.user);
    }
    return response;
  };

  const verifyTotp = async (pendingToken: string, code: string) => {
    const response = await authApi.verifyTotp(pendingToken, code);
    setUser(response.user);
  };

  const register = async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authApi.register(username, password);
      setUser(response.user);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await authApi.logout();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const checkRegistrationStatus = async () => {
    return await authApi.checkRegistrationStatus();
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    verifyTotp,
    register,
    logout,
    checkRegistrationStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
